<template>
	<div class="p-inputgroup">
		<span class="p-float-label mr-2">
			<InputMask
				:id="id"
				:ref="id"
				v-model="localValue"
				mask="99:99:99"
				slotChar="hh:mm:ss"
				placeholder=""
        :class="['p-inputtext-lg', { 'fg-lightCrimson': !complete || !isValid }]"
				@blur="onBlur"
        @keydown.enter="onEnter"
				@focus="focusElement"
				:disabled="disabled"
			/>
			<label :for="id" class="mr-0">{{ label }}</label>
			<span v-if="timeMode > -1" class="p-inputgroup-addon">{{ hourStyle }}</span>
		</span>
	</div>
</template>

<script>
import InputMask from 'primevue/inputmask'

export default {
  name: 'TimeInput',
  components: {
    InputMask
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: 'time-input'
    },
		disabled: {
			type: Boolean,
			default: false
		},
		timeMode: {
			type: Number,
			default: 0
		}
  },
  emits: ['update:modelValue', 'valid'],
  data() {
    return {
      localValue: this.modelValue || '00:00:00',
      isValid: true,
      initValue: null,
      complete: true,
    }
  },
  watch: {
    modelValue(newVal) {
      if (newVal !== this.localValue) {
        this.localValue = newVal || '00:00:00'
      }
    },
    localValue(newVal) {
      this.checkLiveInput(newVal)
      this.$emit('update:modelValue', newVal)
    }
  },
	mounted() {
    if (!this.modelValue || this.modelValue === "") {
      this.localValue = '00:00:00'
      this.initValue = '00:00:00'
      this.$emit('update:modelValue', this.localValue)
      this.$emit('valid', true)
    } else {
      this.initValue = this.modelValue;
    }
  },
	computed: {
    hourStyle() {
      if (this.timeMode === 0) return `Uhr`
			else if (this.timeMode === -1) return ''
      else return `Stunden`
    },
  },
  methods: {
    isInputComplete(val) {
      return /^\d{2}:\d{2}:\d{2}$/.test(val)
    },
    checkLiveInput(val) {
      const fullFormat = /^\d{2}:\d{2}:\d{2}$/.test(val)
      this.isComplete = fullFormat

      if (fullFormat) {
        this.isValid = this.isValidTime(val)
      } else {
        this.isValid = false
      }
    },
    onEnter(event) {
      event.preventDefault()
      this.onBlur()
    },
    onBlur() {
      const input = this.localValue || ''
      const isComplete = this.isInputComplete(input)

      if (!isComplete) {
        this.complete = false
        this.isValid = false
        // return
      }
  
      // Ensure all digits are filled — no underscores, letters, or missing digits
      const isFullyFilled = /^\d{2}:\d{2}:\d{2}$/.test(input)

      if (!isFullyFilled) {
        this.localValue = this.initValue
        this.$emit('update:modelValue', this.localValue)
        this.$emit('valid', false)
        return
      }

      // Proceed with correcting and validating
      const corrected = this.correctTime(input)
      this.localValue = corrected
      this.isValid = this.isValidTime(corrected)
      this.complete = true
      this.$emit('update:modelValue', corrected)
      this.$emit('valid', this.isValidTime(corrected))
    },
    correctTime(val) {
      const safeVal = typeof val === 'string' ? val : '00:00:00'
      const parts = safeVal.split(':')
      // const parts = val.split(':')

      if (parts.length !== 3) return '00:00:00'

      // Extract 2-digit numeric parts with fallback
      const cleanPart = (p) => {
        const digits = p.replace(/\D/g, '').padEnd(2, '0').slice(0, 2)
        return parseInt(digits, 10)
      }

      let hh = cleanPart(parts[0])
      let mm = cleanPart(parts[1])
      let ss = cleanPart(parts[2])

      // Clamp values
      if (isNaN(hh)) hh = 0
      if (isNaN(mm)) mm = 0
      if (isNaN(ss)) ss = 0

      if (hh > 24) hh = 24
      if (hh === 24 && (mm > 0 || ss > 0)) {
        hh = 24
        mm = 0
        ss = 0
      }

      if (mm > 59) mm = 59
      if (ss > 59) ss = 59

      return [
        String(hh).padStart(2, '0'),
        String(mm).padStart(2, '0'),
        String(ss).padStart(2, '0')
      ].join(':')
    },
    isValidTime(val) {
      const [hh, mm, ss] = val.split(':').map(Number)
      return (
        !isNaN(hh) && !isNaN(mm) && !isNaN(ss) &&
        hh >= 0 && hh <= 24 &&
        mm >= 0 && mm <= 59 &&
        ss >= 0 && ss <= 59 &&
        !(hh === 24 && (mm > 0 || ss > 0))
      )
    },
		focusElement(event) {
      if (event !== undefined) {
        event.target.select();
      }
    },
  }
}
</script>

<style scoped>
.p-error {
  font-size: 0.75rem;
  color: #f44336;
}
</style>